/**
 * UI-only transcript payload for what an offer card showed the customer
 * (option chip labels + swap product thumbs). Stamped on tool output and/or a
 * data-* part; convertToModelMessages strips data parts so the model never sees them.
 *
 * Keep type strings in sync with src/lib/cancel-chat-admin/offer-card-details.ts.
 */

export const OFFER_DETAILS_PART_TYPE = "data-offer-details" as const;

export type OfferDetailProduct = {
  title: string;
  imageUrl: string | null;
  price?: number | null;
  variantTitle?: string | null;
};

export type OfferDetailsData = {
  toolCallId: string;
  /** Button labels shown under the card, including the decline chip. */
  options: string[];
  /** Swap (and optionally bundle) products shown on the card. */
  products?: OfferDetailProduct[];
};

export type OfferDetailsPart = {
  type: typeof OFFER_DETAILS_PART_TYPE;
  id: string;
  data: OfferDetailsData;
};

export function createOfferDetailsPart(
  data: OfferDetailsData,
): OfferDetailsPart {
  return {
    type: OFFER_DETAILS_PART_TYPE,
    id: `offer-details-${Math.random().toString(36).slice(2, 10)}`,
    data: {
      toolCallId: data.toolCallId,
      options: data.options.filter((o) => o.trim().length > 0),
      ...(data.products?.length
        ? {
            products: data.products.map((p) => ({
              title: p.title,
              imageUrl: p.imageUrl ?? null,
              price: p.price ?? null,
              variantTitle: p.variantTitle ?? null,
            })),
          }
        : {}),
    },
  };
}

export function serializeOfferProducts(
  products: Array<{
    title: string;
    imageUrl?: string | null;
    price?: number | null;
    variantTitle?: string | null;
  }>,
): OfferDetailProduct[] {
  return products.map((p) => ({
    title: p.title,
    imageUrl: p.imageUrl ?? null,
    price: p.price ?? null,
    variantTitle: p.variantTitle ?? null,
  }));
}
